import type { Branding } from './types.ts';

/**
 * The pre-mount half of branding: fetch the tenant's brand BEFORE React's first
 * paint, so a tenant on purple never sees the product's green first.
 *
 * Why this exists. BrandingProvider used to fetch /api/settings in a mount
 * effect, i.e. after the first paint — and index.css declares the product
 * colours as the --brand-* defaults. Every load therefore painted SupplierClear
 * green for one API round trip, then repainted in the tenant's colour. The apps
 * already hold their first render behind the /api/config boot gate; this runs
 * alongside that gate (API_BASE is static, so it needs no config) and the render
 * waits for both.
 *
 * Kept free of React and the DOM so the ordering rules below are testable in
 * node; branding.tsx owns merging with the fallback and painting the CSS vars.
 */

export type BrandingFetcher = { get<T>(path: string): Promise<T> };
export type BrandingStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** Versioned so a future shape change can simply stop reading old entries. */
export const BRANDING_CACHE_KEY = 'l2u-branding-v1';

/**
 * How long a cold load (nothing cached) waits for the brand before rendering
 * with the product fallback anyway. The fetch is not abandoned: it lands via
 * `pending` and repaints. This only bounds how long a slow API can hold the
 * boot splash on screen.
 */
export const BRANDING_PRELOAD_TIMEOUT_MS = 5000;

export type PreloadedBranding = {
  /** What to paint first. null = nothing known yet; render the product fallback. */
  branding: Partial<Branding> | null;
  /**
   * A fetch still in flight whose result should replace `branding` when it
   * lands: the revalidation behind a cache hit, or a cold fetch that outran the
   * timeout. null when `branding` is already the fresh answer. Never rejects;
   * resolves null on failure.
   */
  pending: Promise<Partial<Branding> | null> | null;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function readCachedBranding(storage: BrandingStorage | null): Partial<Branding> | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(BRANDING_CACHE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isPlainObject(parsed) ? (parsed as Partial<Branding>) : null;
  } catch {
    // Corrupt JSON, or storage that throws on access (private mode, blocked
    // site data). A cache miss, not an error: the fetch still runs.
    return null;
  }
}

export function writeCachedBranding(
  storage: BrandingStorage | null,
  branding: Partial<Branding>,
): void {
  if (!storage) return;
  try {
    storage.setItem(BRANDING_CACHE_KEY, JSON.stringify(branding));
  } catch {
    // Quota or blocked storage: the next load is a cold one, nothing worse.
  }
}

/** localStorage, or null where merely touching it throws (sandboxed iframes). */
export function browserBrandingStorage(): BrandingStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

const TIMED_OUT = Symbol('timed-out');

/**
 * Resolve the brand to paint first. Never rejects.
 *
 * - Cache hit: resolves immediately with the cached brand, and revalidates in
 *   the background through `pending`. A repeat visit paints the tenant's
 *   colours with no network wait at all.
 * - Cache miss: waits for the fetch, up to `timeoutMs`.
 *
 * `api` must be TOKEN-LESS. It is the only writer of the cache, and the cache
 * is per origin, not per user: on a host that serves several tenants, a
 * bearer-resolved brand written here would be shown to the next person to open
 * that host's sign-in page. A token-less response is what that page is entitled
 * to anyway — the tenant the hostname resolves to, or the product brand.
 */
export async function preloadBranding({
  api,
  storage,
  timeoutMs = BRANDING_PRELOAD_TIMEOUT_MS,
}: {
  api: BrandingFetcher;
  storage: BrandingStorage | null;
  timeoutMs?: number;
}): Promise<PreloadedBranding> {
  const cached = readCachedBranding(storage);

  const fetched: Promise<Partial<Branding> | null> = api
    .get<{ branding?: Partial<Branding> }>('/api/settings')
    .then(
      (res) => {
        const branding = isPlainObject(res?.branding) ? res.branding : {};
        writeCachedBranding(storage, branding);
        return branding;
      },
      () => null,
    );

  if (cached) return { branding: cached, pending: fetched };

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), timeoutMs);
  });
  const winner = await Promise.race([fetched, timeout]);
  clearTimeout(timer);

  if (winner === TIMED_OUT) return { branding: null, pending: fetched };
  return { branding: winner, pending: null };
}
